import { useEffect, useMemo, useState } from "react";

import {
  createTourProgram,
  updateTourProgram,
} from "services/api/tourProgramApi";

import { getSchools } from "services/api/schoolApi";
import { showSuccess, showError } from "utils/toast";

const INITIAL_FORM = {
  tourDate: "",
  destination: "",
  taAmount: "0",
  daAmount: "0",
};

/**
 * Extract school records from the response formats
 * already used in this project.
 */
const extractSchools = (response) => {
  const candidates = [
    response?.data?.data?.schools,
    response?.data?.schools,
    response?.data?.data?.records,
    response?.data?.records,
    response?.schools,
    response?.records,
    response?.data?.data,
    response?.data,
  ];

  return candidates.find(Array.isArray) || [];
};

/**
 * Read the logged-in employee ID.
 * Prefer employee.id over user.id because school assignment
 * is based on the employee record.
 */
const getLoggedInEmployeeId = () => {
  try {
    const storedUser = JSON.parse(
      localStorage.getItem("user") || "{}"
    );

    return (
      storedUser?.employee?.id ??
      storedUser?.employeeId ??
      storedUser?.employee?.employeeId ??
      ""
    );
  } catch (error) {
    console.error("Unable to read logged-in user:", error);
    return "";
  }
};

/**
 * Get City/Town name from the school's location data.
 */
const getSchoolCity = (school) => {
  const city =
    school?.location?.city?.name ??
    school?.city?.name ??
    school?.city?.cityName ??
    school?.city?.city_name ??
    school?.cityName ??
    school?.city_name ??
    (typeof school?.city === "string" ? school.city : "");

  return String(city || "").trim();
};

/**
 * Build unique city options from the assigned schools.
 */
const buildCityOptions = (schools) => {
  const cityMap = new Map();

  schools.forEach((school) => {
    const city = getSchoolCity(school);

    if (!city) return;

    const normalizedCity = city.toLocaleLowerCase("en-IN");

    if (!cityMap.has(normalizedCity)) {
      cityMap.set(normalizedCity, city);
    }
  });

  return Array.from(cityMap.values()).sort((a, b) =>
    a.localeCompare(b, "en-IN")
  );
};

const getErrorMessage = (error, fallback) => {
  const message = error?.response?.data?.message;

  return Array.isArray(message)
    ? message.join(", ")
    : message || fallback;
};

export default function TourProgramModal({
  open,
  onClose,
  onSaved,
  item,
  mode = "create",
  isAdmin = false,
}) {
  const [form, setForm] = useState({ ...INITIAL_FORM });

  const [saving, setSaving] = useState(false);
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [cityLoadError, setCityLoadError] = useState("");

  const isViewMode = mode === "view";
  const isEditMode = mode === "edit";

  const total = useMemo(
    () =>
      (Number(form.taAmount) || 0) +
      (Number(form.daAmount) || 0),
    [form.taAmount, form.daAmount]
  );

  const fieldClass =
    "mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none focus:border-brand-500 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500";

  /**
   * Populate the form whenever the modal opens or the
   * selected Tour Program changes.
   */
  useEffect(() => {
    if (!open) return;

    setForm(
      item
        ? {
            tourDate: item.tourDate
              ? String(item.tourDate).slice(0, 10)
              : "",
            destination: item.destination || "",
            taAmount: String(item.taAmount ?? "0"),
            daAmount: String(item.daAmount ?? "0"),
          }
        : { ...INITIAL_FORM }
    );
  }, [open, item]);

  /**
   * Fetch schools filtered by the logged-in employee.
   * Only their assigned schools are used to build the
   * destination city dropdown.
   */
  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    const loadAssignedSchoolCities = async () => {
      if (isViewMode) {
        setLoadingCities(false);
        setCityLoadError("");
        return;
      }

      const employeeId = getLoggedInEmployeeId();

      if (!employeeId) {
        setCities([]);
        setCityLoadError(
          "Logged-in employee profile was not found. Please sign in again."
        );
        setLoadingCities(false);
        return;
      }

      try {
        setLoadingCities(true);
        setCityLoadError("");

        /*
         * Reuse the existing Schools API and its employee filter.
         * Do not fetch all schools and filter them only in the UI.
         */
        const response = await getSchools(
          1,
          500,
          "",
          {
            assignedEmployeeId: String(employeeId),
          }
        );

        if (cancelled) return;

        const schoolRecords = extractSchools(response);
        const cityOptions = buildCityOptions(schoolRecords);

        setCities(cityOptions);

        /*
         * When editing, ensure the selected destination is still
         * one of this employee's assigned-school cities.
         */
        setForm((previous) => {
          if (
            previous.destination &&
            !cityOptions.some(
              (city) =>
                city.toLocaleLowerCase("en-IN") ===
                previous.destination.trim().toLocaleLowerCase("en-IN")
            )
          ) {
            return {
              ...previous,
              destination: "",
            };
          }

          return previous;
        });

        if (cityOptions.length === 0) {
          setCityLoadError(
            "No cities found for your assigned schools. Please contact the administrator."
          );
        }
      } catch (error) {
        if (cancelled) return;

        console.error(
          "Unable to load assigned-school cities:",
          error
        );

        setCities([]);
        setCityLoadError(
          getErrorMessage(
            error,
            "Unable to load cities from your assigned schools."
          )
        );
      } finally {
        if (!cancelled) {
          setLoadingCities(false);
        }
      }
    };

    loadAssignedSchoolCities();

    return () => {
      cancelled = true;
    };
  }, [open, isViewMode]);

  if (!open) return null;

  const change = (key, value) => {
    setForm((previous) => ({
      ...previous,
      [key]: value,
    }));
  };

  const submit = async (event) => {
    event.preventDefault();

    if (isViewMode) return;

    if (!form.tourDate) {
      showError("Tour date is required.");
      return;
    }

    if (!form.destination.trim()) {
      showError("Please select a destination city.");
      return;
    }

    /*
     * Validate the selection against the currently loaded
     * assigned-school city options before sending the request.
     */
    const destinationAllowed = cities.some(
      (city) =>
        city.toLocaleLowerCase("en-IN") ===
        form.destination.trim().toLocaleLowerCase("en-IN")
    );

    if (!destinationAllowed) {
      showError(
        "Please select a city from your assigned schools."
      );
      return;
    }

    const taAmount = Number(form.taAmount);
    const daAmount = Number(form.daAmount);

    if (
      !Number.isFinite(taAmount) ||
      !Number.isFinite(daAmount) ||
      taAmount < 0 ||
      daAmount < 0
    ) {
      showError("TA and DA must be valid non-negative amounts.");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        tourDate: form.tourDate,
        destination: form.destination.trim(),
        taAmount,
        daAmount,
      };

      if (isEditMode && item?.id) {
        await updateTourProgram(item.id, payload);
        showSuccess("Tour Program updated successfully.");
      } else {
        await createTourProgram(payload);
        showSuccess("Tour Program submitted successfully.");
      }

      onClose();
      await onSaved?.();
    } catch (error) {
      showError(
        getErrorMessage(error, "Unable to save Tour Program.")
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
        {/* Header */}
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-xl font-bold text-navy-700">
            {isViewMode
              ? "Tour Program Details"
              : isEditMode
              ? "Edit Tour Program"
              : "Add Tour Program"}
          </h2>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3 py-1 text-xl text-gray-500 hover:bg-gray-100"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Employee information */}
        {item && (
          <div className="mb-4 rounded-lg bg-gray-50 p-3 text-sm">
            <p>
              <strong>Employee:</strong>{" "}
              {item.employeeName || "-"}
            </p>

            <p>
              <strong>Employee Code:</strong>{" "}
              {item.employeeCode || "-"}
            </p>

            <p>
              <strong>Status:</strong>{" "}
              {item.status || "-"}
            </p>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          {/* Tour Date */}
          <div>
            <label
              htmlFor="tourDate"
              className="text-sm font-semibold text-gray-700"
            >
              Tour Date *
            </label>

            <input
              id="tourDate"
              type="date"
              required
              value={form.tourDate}
              disabled={isViewMode || saving}
              onChange={(event) =>
                change("tourDate", event.target.value)
              }
              className={fieldClass}
            />
          </div>

          {/* Destination: assigned school cities only */}
          <div>
            <label
              htmlFor="destination"
              className="text-sm font-semibold text-gray-700"
            >
              Destination City / Town *
            </label>

            {isViewMode ? (
              <div className={`${fieldClass} min-h-11`}>
                {form.destination || "-"}
              </div>
            ) : (
              <select
                id="destination"
                required
                value={form.destination}
                disabled={
                  saving ||
                  loadingCities ||
                  cities.length === 0
                }
                onChange={(event) =>
                  change("destination", event.target.value)
                }
                className={fieldClass}
              >
                <option value="">
                  {loadingCities
                    ? "Loading assigned-school cities..."
                    : cities.length > 0
                    ? "Select Destination City"
                    : "No assigned-school cities available"}
                </option>

                {cities.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>
            )}

            {!isViewMode && cityLoadError && (
              <p className="mt-2 text-xs text-red-600">
                {cityLoadError}
              </p>
            )}

            {!isViewMode && cities.length > 0 && (
              <p className="mt-1 text-xs text-gray-500">
                Only cities of schools assigned to your employee
                profile are available.
              </p>
            )}
          </div>

          {/* TA / DA */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="taAmount"
                className="text-sm font-semibold text-gray-700"
              >
                TA Amount (₹) *
              </label>

              <input
                id="taAmount"
                type="number"
                min="0"
                step="0.01"
                required
                value={form.taAmount}
                disabled={isViewMode || saving}
                onChange={(event) =>
                  change("taAmount", event.target.value)
                }
                className={fieldClass}
              />
            </div>

            <div>
              <label
                htmlFor="daAmount"
                className="text-sm font-semibold text-gray-700"
              >
                DA Amount (₹) *
              </label>

              <input
                id="daAmount"
                type="number"
                min="0"
                step="0.01"
                required
                value={form.daAmount}
                disabled={isViewMode || saving}
                onChange={(event) =>
                  change("daAmount", event.target.value)
                }
                className={fieldClass}
              />
            </div>
          </div>

          {/* Total */}
          <div className="rounded-xl bg-blue-50 p-4">
            <p className="text-sm font-medium text-blue-700">
              Total TA + DA
            </p>

            <p className="mt-1 text-2xl font-bold text-blue-900">
              ₹
              {total.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </p>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 disabled:opacity-60"
            >
              {isViewMode ? "Close" : "Cancel"}
            </button>

            {!isViewMode && (
              <button
                type="submit"
                disabled={
                  saving ||
                  loadingCities ||
                  cities.length === 0
                }
                className="rounded-lg bg-brand-500 px-5 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving
                  ? "Saving..."
                  : isEditMode
                  ? "Update Program"
                  : "Submit Program"}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}